<template>
  <div
    v-if="hasPermission('analyses.defaults.view')"
    id="admin-default-analyses-page"
  >
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableRemote
      v-else
      ref="tableRemoteRef"
      :iconHeader="hasPermission('analyses.defaults.add') ? 'add' : null"
      :items="displayedDefaultAnalyses"
      :headers="tableHeaders"
      :itemActions="getItemActions"
      :title="$t('defaultAnalyses.manageDefaultAnalyses')"
      :centerColumns="['action']"
      :notSortableColumns="['action']"
      :showRowIcon="false"
      :isCustomIconNext="true"
      :disablePagination="true"
      :pagination="pagination"
      :isCustomIcon="true"
      :search="queryParams.search"
      isSearchable
      :loading="isFetching"
      isSelectable
      hideDefaultFooter
      @iconAction="onClickAction"
      @click-icon-header="onClickAdd"
      @sendQueryParams="onQueryParams"
      @search="onSearch"
    />
  </div>
  <div v-else>
    <NoPermissionAlert :message="$t('permissions.error')" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import AddDefaultAnalysisForm from './AddDefaultAnalysisForm.vue';

export default {
    components: {
        VTableRemote,
        NoPermissionAlert,
    },
    data() {
        return {
            defaultAnalyses: [],
            loading: false,
            isFetching: false,
            pagination: {},
            queryParams: {
                perPage: 25,
                search: '',
                pagination: false,
            },
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'userLang',
        ]),
        tableHeaders() {
            return [
                'networkTypeName',
                'componentTypeName',
                'analyses',
            ].map(header => ({
                text: this.$t(`defaultAnalyses.labels.${header}`),
                value: header,
            }));
        },
        displayedDefaultAnalyses() {
            return this.defaultAnalyses?.reduce((result, networkType) => result.concat(
                networkType.componentTypes.map(componentType => ({
                    networkTypeId: networkType.id,
                    networkTypeName: networkType.name,
                    componentTypeId: componentType.id,
                    componentTypeName: componentType.name,
                    analysisTypeId: networkType.id,
                    analyses: componentType.nbrAnalysisTypes,
                })),
            ), []);
        },
        getItemActions() {
            const actions = [];
            if (this.hasPermission('analyses.defaults.edit')) {
                actions.push({
                    name: this.$t('forms.labels.edit'),
                    icon: 'edit',
                    mode: 'edit',
                    iconAndTextColor: '#26384B',
                });
            }
            if (this.hasPermission('analyses.defaults.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'clear',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },
    },
    watch: {
        'queryParams.search': function getAnalyses() {
            this.fetchQuery();
        },
        userLang: function getAnalyses() {
            this.getDefaultAnalyses();
        },
    },
    async created() {
        this.loading = true;
        await this.getDefaultAnalyses();
        this.loading = false;
    },
    methods: {
        async getDefaultAnalyses() {
            if (this.isFetching) return;
            this.isFetching = true;
            this.defaultAnalyses = [];
            const { data } = await this.$rq.getDefaultAnalysesStats(this.queryParams);
            this.defaultAnalyses = [...this.defaultAnalyses, ...data.networkTypes] || [];
            this.isFetching = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('defaultAnalyses.labels.addDefaultAnalysis'),
                component: Vue.component('AddDefaultAnalysisForm', AddDefaultAnalysisForm),
                onConfirm: requestPayload => this.addDefaultAnalysisRequest(requestPayload),
            });
        },
        onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
            } else {
                switch (action.mode) {
                    case 'clear':
                        this.$root.$emit('showModal', {
                            title: this.$t('defaultAnalyses.remove'),
                            component: Vue.component('ConfirmForm', ConfirmForm),
                            payload: { msg: this.$t('defaultAnalyses.confirmRemove') },
                            onConfirm: async requestPayload => {
                                this.removeDefaultAnalyses(item, requestPayload);
                            },
                            isForm: false,
                        });
                        break;
                    case 'edit':
                        this.$root.$emit('showModal', {
                            title: this.$t('defaultAnalyses.edit'),
                            component: Vue.component('EditAddDefaultAnalysisForm', AddDefaultAnalysisForm),
                            payload: {
                                analysis: {
                                    networkType: item.networkTypeId,
                                    componentType: item.componentTypeId,
                                },
                            },
                            onConfirm: async requestPayload => {
                                this.editDefaultAnalyses(item, requestPayload);
                            },
                            isForm: false,
                        });
                        break;
                    default:
                        break;
                }
            }
        },
        async removeDefaultAnalyses(item) {
            const { componentTypeId, networkTypeId } = item;
            const queryParams = {
                componentType: componentTypeId,
                networkType: networkTypeId !== 'any' ? networkTypeId : undefined,
            };

            const { data } = await this.$rq.getDefaultAnalyses(queryParams);
            const promises = data.defaultAnalysisTypes?.map(({ id }) => this.$rq.deleteDefaultAnalysis(id));

            await Promise.all(promises);
            this.$toasted.global.toast_success({
                message: this.$t('msg.defaultAnalysisRemoved'),
            });
            this.fetchQuery();
        },
        async addDefaultAnalysisRequest(requestPayload) {
            const { analysisTypes, componentType, networkType } = requestPayload;
            const promises = analysisTypes.map(analysisType => this.addDefaultAnalysis({ componentType, networkType, analysisType }));

            await Promise.all(promises);
            this.$toasted.global.toast_success({
                message: this.$t('msg.defaultAnalysisAdded'),
            });
            this.fetchQuery();
        },
        async editDefaultAnalyses(item, requestPayload) {
            const bodyParams = {
                componentType: requestPayload.componentType,
                networkType: requestPayload.networkType || 'any',
                analysisTypeIds: requestPayload.analysisTypes,
                oldComponentType: item.componentTypeId,
                oldNetworkType: item.networkTypeId,
            };

            const { data } = await this.$rq.putDefaultAnalysis(bodyParams);

            if (data?.currentResults) {
                this.$toasted.global.toast_success({
                    message: this.$t('msg.defaultAnalysisEdited'),
                });
            }
            this.fetchQuery();
        },
        addDefaultAnalysis(bodyParams) {
            return this.$rq.addDefaultAnalysis(bodyParams);
        },
        onQueryParams(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
            };
            this.getDefaultAnalyses();
        },
        onSearch(search) {
            this.queryParams.search = search;
            this.getDefaultAnalyses();
        },
        fetchQuery() {
            this.getDefaultAnalyses();
        },
    },
};
</script>
